import { useEffect, useState } from 'react'
import { useRestaurantStore, useRewardsStore, isRetriableRequestError } from '@restaurant/core'
import { apiClient } from '@restaurant/api-client'
import type { Offer } from '@restaurant/core'
import { safeGetLogger } from '@restaurant/logging'
import { isShowcase } from '../showcase/showcase'


/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Transform a snake_case API offer response to the camelCase Offer model.
 */
function transformOffer(raw: any): Offer {
  return {
    id: raw.id,
    restaurantId: raw.business_id,
    title: raw.title,
    description: raw.description ?? '',
    type: raw.type,
    value: raw.value,
    code: raw.code ?? undefined,
    imageUrl: raw.image_url ?? undefined,
    minimumPurchase: raw.minimum_purchase ?? 0,
    maxDiscount: raw.max_discount ?? undefined,
    startDate: raw.start_date,
    endDate: raw.end_date ?? null,
    numberOfUses: raw.number_of_uses ?? undefined,
    usagePerCustomer: raw.usage_per_customer ?? 1,
    redemptionCount: raw.redemption_count ?? 0,
    displayOrder: raw.display_order ?? 0,
    requirementType: raw.requirement_type ?? 'all',
    products: (raw.products ?? []).map((p: any) => ({
      id: p.id,
      offerId: p.offer_id,
      menuItemId: p.menu_item_id,
      menuItem: p.menu_item,
      variants: p.variants ?? [],
      isRequired: p.is_required,
      createdAt: p.created_at,
    })),
    timeConstraints: (raw.time_constraints ?? []).map((tc: any) => ({
      id: tc.id,
      daysOfWeek: tc.days_of_week,
      startDatetime: tc.start_datetime,
      endDatetime: tc.end_datetime,
    })),
    storeLocations: (raw.store_locations ?? []).map((sl: any) => ({
      storeId: sl.store_id,
      storeName: sl.store_name,
    })),
    isActive: true, // API doesn't provide this — active if not removed/expired
    isPublic: raw.is_public ?? true,
    requiresRewardMembership: raw.requires_reward_membership ?? false,
    minimumTier: raw.minimum_tier ?? undefined,
    removedAt: raw.removed_at ?? undefined,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  }
}

/** Backoff before each retry. Length also caps the retry count. */
const RETRY_DELAYS_MS = [1000, 3000]

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

/**
 * Hook to fetch and populate offers data from Flask API
 * Fetches offers for the current restaurant's business
 */
export function useOffersData() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { currentRestaurant } = useRestaurantStore()
  const { setAvailableOffers, setOffersLoadFailed } = useRewardsStore()

  useEffect(() => {
    if (isShowcase()) return
    // Guards against a retry landing after unmount or after the restaurant
    // changed — with backoff the in-flight window is now seconds, not one request.
    let cancelled = false

    const fetchOffersData = async () => {
      if (!currentRestaurant?.id) {
        safeGetLogger()?.debug('No restaurant ID, skipping offers', { domain: 'offers' })
        return
      }

      const businessId = currentRestaurant.id
      safeGetLogger()?.debug('Fetching offers', { domain: 'offers', businessId })

      setIsLoading(true)
      setError(null)

      for (let attempt = 0; ; attempt++) {
        try {
          // Call Flask endpoint to get offers by business ID
          // The endpoint returns complete offers with all related data when for_mobile=true
          const response = await apiClient.get<any[]>(
            `/business/${businessId}/offers`,
            { params: { for_mobile: true, include_all: true } }
          )
          if (cancelled) return

          const rawOffers = response || []
          const offers: Offer[] = rawOffers.map(transformOffer)
          safeGetLogger()?.offers('loaded', { count: offers.length })

          // Update the store with offers
          setAvailableOffers(offers)
          break

        } catch (err) {
          if (cancelled) return
          const errorMessage = err instanceof Error ? err.message : 'Failed to load offers'

          // Undefined once `attempt` runs past the end of the table — that is the
          // retry budget expiring.
          const retryDelayMs = RETRY_DELAYS_MS[attempt]
          if (retryDelayMs !== undefined && isRetriableRequestError(err)) {
            safeGetLogger()?.warn('Offers load failed, retrying', {
              domain: 'offers', error: errorMessage, attempt: attempt + 1,
            })
            await sleep(retryDelayMs)
            if (cancelled) return
            continue
          }

          safeGetLogger()?.error('Offers load failed', {
            domain: 'offers', error: errorMessage, attempts: attempt + 1,
          })
          setError(errorMessage)
          // Set empty offers array so tabs are hidden on error, then record that
          // this emptiness is a failure rather than a genuinely offer-less
          // restaurant. Order matters — setAvailableOffers clears the flag.
          setAvailableOffers([])
          setOffersLoadFailed(true)
          break
        }
      }

      setIsLoading(false)
    }

    void fetchOffersData()
    return () => { cancelled = true }
  }, [currentRestaurant?.id, setAvailableOffers, setOffersLoadFailed])

  return { isLoading, error }
}
